/**
 * DatasetsTab Component
 *
 * Displays a table of datasets with filtering, creation, and management capabilities.
 * Integrates with the AI backend API for dataset operations.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Progress } from "@/shared/ui/progress";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import {
  Upload,
  Trash2,
  Eye,
  MoreVertical,
  Loader2,
  Database,
  PlusCircle,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { getDatasets, deleteDataset } from "../api/datasets";
import { DatasetCreatorDialog } from "./DatasetCreatorDialog";
import type { Dataset } from "../types";
import type { DatasetResponse } from "../types/dataset";

interface DatasetsTabProps {
  datasets: Dataset[];
  showMockData: boolean;
  onViewDataset?: (datasetId: string) => void;
}

export function DatasetsTab({ datasets, showMockData, onViewDataset }: DatasetsTabProps) {
  const queryClient = useQueryClient();
  const [stationFilter, setStationFilter] = useState<string>("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<DatasetResponse | null>(null);

  // Fetch datasets from API when not showing mock data
  const {
    data: apiDatasets,
    isLoading,
    isError,
    error,
  } = useQuery<DatasetResponse[]>({
    queryKey: ["ai", "datasets"],
    queryFn: getDatasets,
    enabled: !showMockData,
  });

  // Delete mutation
  const { mutate: deleteMutate, isPending: isDeleting } = useMutation({
    mutationFn: deleteDataset,
    onSuccess: () => {
      toast.success("Dataset deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["ai", "datasets"] });
      setDeleteCandidate(null);
    },
    onError: (err) => {
      toast.error(`Failed to delete dataset: ${err.message}`);
      setDeleteCandidate(null);
    },
  });

  // Get display name from connector (e.g., "@InfluxDB.bucket" -> "bucket")
  const getStationFromConnector = (connectorName: string) => {
    return connectorName.replace("@InfluxDB.", "");
  };

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case "ready":
        return "default";
      case "processing":
      case "labeling":
        return "secondary";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  // Get unique stations from datasets for filter
  const getUniqueStations = () => {
    if (showMockData) {
      return [...new Set(datasets.map((d) => d.station))];
    }
    if (apiDatasets) {
      return [...new Set(apiDatasets.map((d) => getStationFromConnector(d.connectorName)))];
    }
    return [];
  };

  // Filter datasets by station
  const getFilteredDatasets = () => {
    if (showMockData) {
      if (stationFilter === "all") return datasets;
      return datasets.filter((d) => d.station === stationFilter);
    }
    if (apiDatasets) {
      if (stationFilter === "all") return apiDatasets;
      return apiDatasets.filter((d) => getStationFromConnector(d.connectorName) === stationFilter);
    }
    return [];
  };

  const uniqueStations = getUniqueStations();
  const filteredDatasets = getFilteredDatasets();

  // Render mock data table rows
  const renderMockDataRows = () => {
    return (datasets as Dataset[]).map((dataset) => (
      <TableRow key={dataset.id}>
        <TableCell className="font-medium">{dataset.name}</TableCell>
        <TableCell>
          <Badge variant="outline">{dataset.station}</Badge>
        </TableCell>
        <TableCell>{dataset.samples.toLocaleString()}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Progress
              value={(dataset.labeled / dataset.samples) * 100}
              className="w-24 h-2"
            />
            <span className="text-xs text-muted-foreground">
              {Math.round((dataset.labeled / dataset.samples) * 100)}%
            </span>
          </div>
        </TableCell>
        <TableCell>
          <Badge
            variant={
              dataset.status === "ready"
                ? "default"
                : dataset.status === "labeling"
                  ? "secondary"
                  : "outline"
            }
          >
            {dataset.status}
          </Badge>
        </TableCell>
        <TableCell className="text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDataset?.(dataset.id)}
          >
            View
          </Button>
        </TableCell>
      </TableRow>
    ));
  };

  // Render API data table rows
  const renderApiDataRows = () => {
    if (isLoading) {
      return [...Array(3)].map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-5 w-32" /></TableCell>
          <TableCell><Skeleton className="h-5 w-24" /></TableCell>
          <TableCell><Skeleton className="h-5 w-20" /></TableCell>
          <TableCell><Skeleton className="h-5 w-32" /></TableCell>
          <TableCell><Skeleton className="h-5 w-16" /></TableCell>
          <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
        </TableRow>
      ));
    }

    if (isError) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="h-48 text-center">
            <Database className="mx-auto h-12 w-12 text-destructive/50" />
            <h3 className="mt-4 text-lg font-semibold">Failed to load datasets</h3>
            <p className="text-muted-foreground">{(error as Error)?.message || "Unknown error"}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => queryClient.invalidateQueries({ queryKey: ["ai", "datasets"] })}
            >
              Retry
            </Button>
          </TableCell>
        </TableRow>
      );
    }

    const apiFilteredDatasets = filteredDatasets as DatasetResponse[];

    if (apiFilteredDatasets.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="h-48 text-center">
            <Database className="mx-auto h-12 w-12 text-muted-foreground/30" />
            <h3 className="mt-4 text-lg font-semibold">No datasets found</h3>
            <p className="text-muted-foreground">Get started by creating a new dataset.</p>
          </TableCell>
        </TableRow>
      );
    }

    return apiFilteredDatasets.map((dataset) => {
      const samples = dataset.rowCount ?? 0;
      const labeled = 0; // TODO: Get from labels API when available
      const progress = samples > 0 ? (labeled / samples) * 100 : 0;

      return (
        <TableRow key={dataset.id}>
          <TableCell className="font-medium">{dataset.name}</TableCell>
          <TableCell>
            <Badge variant="outline">
              {getStationFromConnector(dataset.connectorName)}
            </Badge>
          </TableCell>
          <TableCell>
            {typeof dataset.rowCount === "number"
              ? dataset.rowCount.toLocaleString()
              : "—"}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2">
              <Progress value={progress} className="w-24 h-2" />
              <span className="text-xs text-muted-foreground">
                {Math.round(progress)}%
              </span>
            </div>
          </TableCell>
          <TableCell>
            <Badge variant={getStatusVariant(dataset.status)}>
              {dataset.status}
            </Badge>
          </TableCell>
          <TableCell className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onViewDataset?.(dataset.id)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View & Label
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setDeleteCandidate(dataset)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={stationFilter} onValueChange={setStationFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by Station" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stations</SelectItem>
              {uniqueStations.map((station) => (
                <SelectItem key={station} value={station}>
                  {station}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <PlusCircle className="h-4 w-4 mr-2" />
          New Dataset
        </Button>
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dataset Name</TableHead>
              <TableHead>Station</TableHead>
              <TableHead>Samples</TableHead>
              <TableHead>Labeling Progress</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {showMockData ? renderMockDataRows() : renderApiDataRows()}
          </TableBody>
        </Table>
      </Card>

      {/* Create Dataset Dialog */}
      <DatasetCreatorDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={() => setDeleteCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Dataset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteCandidate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCandidate && deleteMutate(deleteCandidate.id)}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
